import React from 'react';
import { Camera, Plane, Video, Brush, FileText, Inbox, ShieldCheck, Code, Globe, Users, Circle, Check, RotateCcw, Trash2, ExternalLink, Pencil } from 'lucide-react';
import { DELIVERABLE_TYPE_META, deliverableStatusMeta, validDeliverableTransitions } from '../../lib/projects';

const ICON_MAP = { camera: Camera, plane: Plane, video: Video, brush: Brush, 'file-text': FileText, inbox: Inbox, 'shield-check': ShieldCheck, code: Code, globe: Globe, users: Users, circle: Circle };

// asset_urls_json on the deliverable row stores the media library link(s)
// captured by the Shoots page "Mark complete" dialog. Parse defensively;
// malformed JSON or non-array values just yield no links.
function parseAssetUrls(jsonText) {
  if (!jsonText) return [];
  try {
    const parsed = JSON.parse(jsonText);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((x) => typeof x === 'string' && x.trim());
  } catch { return []; }
}

export default function DeliverableRow({ deliverable, onStatus, onComplete, onDelete, onEditMediaLink }) {
  const typeMeta = DELIVERABLE_TYPE_META[deliverable.type] || { label: deliverable.type, icon: 'circle' };
  const Icon = ICON_MAP[typeMeta.icon] || Circle;
  const statusMeta = deliverableStatusMeta(deliverable.status);
  const isDone = deliverable.status === 'delivered';
  const nextOptions = validDeliverableTransitions(deliverable.status);
  return (
    <div className={`flex items-start gap-3 p-3 rounded-xl border border-white/[0.06] bg-white/[0.02] ${isDone ? 'opacity-70' : ''}`}>
      <div className="shrink-0 w-8 h-8 rounded-lg bg-white/[0.04] border border-white/[0.06] flex items-center justify-center text-gray-400">
        <Icon className="w-3.5 h-3.5" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="text-[13px] text-white font-medium">{typeMeta.label}</div>
          <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] uppercase tracking-wider border ${statusMeta.tone}`}>
            {statusMeta.label}
          </span>
        </div>
        {deliverable.description && <div className="text-[12px] text-gray-500 mt-0.5">{deliverable.description}</div>}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1 text-[11px] text-gray-500">
          {deliverable.scheduledDate && <span>scheduled {deliverable.scheduledDate}</span>}
          {deliverable.completedDate && <span>delivered {deliverable.completedDate}</span>}
          {deliverable.assigneeName && <span>→ {deliverable.assigneeName}</span>}
        </div>
        {(() => {
          const urls = parseAssetUrls(deliverable.assetUrlsJson);
          if (urls.length === 0) return null;
          return (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {urls.map((u, i) => (
                <a
                  key={u}
                  href={u}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={u}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] border border-[#B0FC38]/30 bg-[#B0FC38]/10 text-[#B0FC38] hover:bg-[#B0FC38]/20 hover:text-white hover:border-[#B0FC38]/50 transition-colors"
                >
                  <ExternalLink className="w-3 h-3" />
                  {urls.length > 1 ? `Media library ${i + 1}` : 'Media library'}
                </a>
              ))}
            </div>
          );
        })()}
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {isDone ? (
          <>
            {onEditMediaLink && (
              <button
                type="button"
                onClick={() => onEditMediaLink(deliverable)}
                title="Edit or remove media library link"
                data-testid="deliverable-edit-media-link"
                className="p-1.5 rounded-lg text-gray-500 hover:text-[#B0FC38] hover:bg-[#B0FC38]/10 transition-colors"
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
            )}
            {onStatus && nextOptions.length > 0 && (
              <button
                type="button"
                onClick={() => onStatus(deliverable, nextOptions[0])}
                title={`Reopen · move back to ${deliverableStatusMeta(nextOptions[0]).label}`}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] text-amber-300/90 border border-amber-500/25 bg-amber-500/[0.06] hover:text-amber-200 hover:border-amber-500/40 hover:bg-amber-500/[0.12] transition-colors"
              >
                <RotateCcw className="w-3 h-3" /> Reopen
              </button>
            )}
          </>
        ) : (
          <>
            {onStatus && nextOptions.length > 0 && (
              <select
                value={deliverable.status}
                onChange={(e) => e.target.value !== deliverable.status && onStatus(deliverable, e.target.value)}
                className="bg-white/[0.03] border border-white/[0.08] rounded-lg px-2 py-1 text-[11px] text-white"
                aria-label="Change status"
              >
                <option value={deliverable.status} hidden>{statusMeta.label}</option>
                {nextOptions.map((s) => <option key={s} value={s}>{deliverableStatusMeta(s).label}</option>)}
              </select>
            )}
            {onComplete && (
              <button onClick={() => onComplete(deliverable)} title="Mark delivered"
                className="p-1.5 rounded-lg text-gray-500 hover:text-emerald-300 hover:bg-emerald-500/10">
                <Check className="w-3.5 h-3.5" />
              </button>
            )}
          </>
        )}
        {onDelete && (
          <button onClick={() => onDelete(deliverable)} title="Delete"
            className="p-1.5 rounded-lg text-gray-600 hover:text-red-400 hover:bg-red-500/10">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}
